import React, { useState } from 'react';
import { DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import AsyncState from '../../../components/AsyncState';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../translations';
import { downloadAuthenticatedFile } from '../../../utils/api';
import { describeApiError, fillTemplate, formatBytes, formatDateTime } from '../../../utils/format';
import { lifecycleUrl, requestJson } from '../../../utils/lifecycle';

const CATEGORIES = ['logo', 'icon', 'splash', 'guideline', 'reference', 'other'];
const ACCEPTED_FILES = '.png,.jpg,.jpeg,.webp,.gif,.svg,.pdf,.zip,.apk,.aab,.fig,.ai,.psd,.doc,.docx,.xls,.xlsx,.txt';

export function downloadAsset(appId, asset) {
  return downloadAuthenticatedFile(lifecycleUrl(`/custom-apps/${appId}/assets/${asset.id}`), asset.filename);
}

export default function AssetsTab({ appId, assets, canUpload, onChanged }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [category, setCategory] = useState('logo');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: '', text: '' });
  const items = assets.data?.items || [];
  const hasBrandAsset = items.some((item) => ['logo', 'icon'].includes(item.category));

  const upload = async (event) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setMessage({ kind: '', text: '' });
    const body = new FormData();
    body.append('file', file);
    body.append('category', category);
    try {
      await requestJson(`/custom-apps/${appId}/assets`, { method: 'POST', body });
      setFile(null);
      event.target.reset();
      setMessage({ kind: 'ok', text: t.fileUploaded });
      await onChanged();
    } catch (error) {
      if (error.status !== 401) setMessage({ kind: 'error', text: describeApiError(error, t, error.message) });
    } finally {
      setBusy(false);
    }
  };

  const download = async (asset) => {
    try {
      await downloadAsset(appId, asset);
    } catch (error) {
      if (error.status !== 401) setMessage({ kind: 'error', text: describeApiError(error, t, error.message) });
    }
  };

  return <section className="card stack">
    {canUpload && <form className="tile form-grid" onSubmit={upload}>
      <div className="field">
        <label className="label" htmlFor="asset-category">{t.fileType}</label>
        <select id="asset-category" className="select" value={category} onChange={(event) => setCategory(event.target.value)}>
          {CATEGORIES.map((key) => <option key={key} value={key}>{t.categoryLabel[key]}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="label" htmlFor="asset-file">{t.chooseFile}</label>
        <input id="asset-file" type="file" accept={ACCEPTED_FILES} onChange={(event) => setFile(event.target.files[0] || null)} />
      </div>
      <div className="field field--wide"><button className="btn btn--primary" type="submit" disabled={!file || busy}><UploadSimple size={18} aria-hidden="true" /> {busy ? t.uploadingFile : t.uploadFile}</button></div>
    </form>}
    {message.text && <p className={message.kind === 'error' ? 'notice notice--error' : 'hint'} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</p>}
    {!hasBrandAsset && !assets.loading && <p className="notice notice--warn" role="note">{t.noLogoYet}</p>}
    <AsyncState state={assets} retry={assets.retry} isEmpty={items.length === 0} empty={<p className="hint">{t.noFiles}</p>}>
      <ul className="row-list">
        {items.map((item) => <li key={item.id}>
          <button className="row" type="button" onClick={() => download(item)} aria-label={fillTemplate(t.downloadFile, { name: item.filename })}>
            <span className="row__main">
              <span className="row__title">{item.filename}</span>
              <span className="row__meta">{t.categoryLabel[item.category] || t.categoryLabel.other} · {formatBytes(item.size)} · {fillTemplate(t.uploadedBy, { name: item.uploadedBy?.name || t.teamMember })} · {formatDateTime(item.createdAt, language)}</span>
            </span>
            <DownloadSimple size={20} aria-hidden="true" />
          </button>
        </li>)}
      </ul>
    </AsyncState>
  </section>;
}
